import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PurchaseStatus } from '@construction/shared';
import { purchasesApi, type Site } from '@/api/endpoints';
import { Alert, EmptyState, Spinner } from '@/components/ui';
import { PendingCard, usePendingItems } from '@/features/upload-queue';
import { formatMoney } from '@/lib/format';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { PurchaseFilters, purchaseEmptyKey } from './PurchaseFilters';
import { PurchaseDetail, PurchaseList } from './PurchaseViews';

/**
 * 我在这个工地记过的采购（CR-042 收进采购页的「我的」tab，CR-082 重做）。
 *
 * 工人打开这一页真正想知道的是**「我垫的钱报了没有」**，所以按状态分页签（CR-031 / CR-050）。
 * 列表按**当前工地**取（`?siteId=`，CR-042）。和「本工地」同一种列表和详情（`PurchaseViews`），只是看不到审批按钮。
 *
 * **筛选和「本工地」是同一套**（CR-048，客户原话「我的tab里复用 本工地tab的筛选」）：
 * 区间和状态放在页面上，切 tab 不变。区间由服务端筛；状态在前端分 ——
 * 每个页签上要写着几笔，一次拿回这段区间的全部状态才数得出来。
 *
 * **待批准 / 待报销不按日期**（CR-049）：这两个页签另各取一份不带区间的，数字和列表都用它。
 * 页签和「本工地」一样（CR-050），但**不自动落在待处理** —— 工人不审批，打开是「全部记录」。
 */
export function MyPurchasesList({
  site,
  composing,
  range,
  onRangeChange,
  status,
  onStatusChange,
  today,
  leading,
}: {
  site: Site;
  composing: boolean;
  range: { from: string; to: string };
  onRangeChange: (range: { from: string; to: string }) => void;
  status: PurchaseStatus | null;
  onStatusChange: (status: PurchaseStatus | null) => void;
  today: string;
  /** 工具条最左边：本工地 / 我的 */
  leading?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const [openId, setOpenId] = useState<string | null>(null);

  const mine = useQuery({
    queryKey: ['my-purchases', site.id, range.from, range.to],
    queryFn: () => purchasesApi.mine(site.id, { from: range.from, to: range.to }),
  });

  // 待批准 / 待报销不按日期（CR-049）。自己名下还没结清的不会有几百笔，300 条上限够用
  const openPending = useQuery({
    queryKey: ['my-purchases', site.id, 'open', PurchaseStatus.PENDING],
    queryFn: () => purchasesApi.mine(site.id, { status: PurchaseStatus.PENDING }),
  });
  const openApproved = useQuery({
    queryKey: ['my-purchases', site.id, 'open', PurchaseStatus.APPROVED],
    queryFn: () => purchasesApi.mine(site.id, { status: PurchaseStatus.APPROVED }),
  });

  // 还在队列里的采购（CR-037）：服务端还没有它们，摆在最上面标「上传中」
  const pending = usePendingItems('PURCHASE', site.id);

  const all = mine.data ?? [];
  const openQuery =
    status === PurchaseStatus.PENDING ? openPending : status === PurchaseStatus.APPROVED ? openApproved : null;
  const active = openQuery ?? mine;
  const list = openQuery ? (openQuery.data ?? []) : status ? all.filter((p) => p.status === status) : all;
  const total = list.reduce((sum, p) => sum + p.amountCents, 0);
  const opened = list.find((p) => p.id === openId) ?? null;

  return (
    <>
      <PurchaseFilters
        range={range}
        onRangeChange={onRangeChange}
        today={today}
        status={status}
        onStatusChange={onStatusChange}
        counts={{ PENDING: openPending.data?.length, APPROVED: openApproved.data?.length }}
        leading={leading}
        summary={
          list.length > 0 &&
          t('purchases.summaryLine', { count: list.length, amount: formatMoney(total, i18n.language, site.currency) })
        }
      />

      {active.isLoading && (
        <div className="flex justify-center py-8 text-ink-3">
          <Spinner />
        </div>
      )}
      {active.isError && <Alert>{toMessage(active.error)}</Alert>}

      {pending.length > 0 && (
        <ul className="mb-3 flex flex-col gap-2">
          {pending.map((item) => (
            <PendingCard
              key={item.id}
              item={item}
              title={formatMoney(Number(item.payload.amountCents ?? 0), i18n.language, site.currency)}
              subtitle={[item.payload.category, item.payload.vendor].filter(Boolean).join(' · ') || undefined}
            />
          ))}
        </ul>
      )}

      {active.data && list.length === 0 && pending.length === 0 && !composing && (
        <EmptyState title={t(purchaseEmptyKey(status))} hint={t('purchases.emptyWorkerHint')} />
      )}

      {/* 自己的采购不写人名：一行是 商家 · 类别，日期；右边金额和状态 */}
      <PurchaseList purchases={list} timezone={site.timezone} onOpen={setOpenId} showUser={false} />
      <PurchaseDetail purchase={opened} timezone={site.timezone} onClose={() => setOpenId(null)} readOnly />
    </>
  );
}
